import { useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/API";
import { extractErrorMessage } from "../utils/errorMessage";

const inputClass ="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-role-primary focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:bg-slate-50";

const labelClass ="text-sm font-medium text-slate-700";

export default function ForgotPassword() {
 const navigate = useNavigate();

 const [email, setEmail] = useState("");
 const [submitting, setSubmitting] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [sent, setSent] = useState(false);

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);

 if (!email.trim()) {
 setError("Email is required.");
 return;
 }

 setSubmitting(true);
 try {
 await API.post("/api/auth/forgot-password", {
 email: email.trim(),
 });
 setSent(true);
 } catch (err: unknown) {
 console.error("ForgotPassword error: ", err);
 setError(extractErrorMessage(err,"Failed to request password reset. Please try again."));
 } finally {
 setSubmitting(false);
 }
 };

 return (
 <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 text-slate-900">
 {/* Backdrop */}
 <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40"/>
 <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-emerald-300/30 blur-3xl"/>
 <div className="pointer-events-none absolute -bottom-48 -left-32 h-[34rem] w-[34rem] rounded-full bg-emerald-300/30 blur-3xl"/>
 <div className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-emerald-300/20 blur-3xl"/>

 <div className="relative mx-auto flex min-h-screen max-w-6xl items-center px-4 py-10 sm:px-6 lg:px-8">
 <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-14">
 {/* LEFT — branding */}
 <aside className="relative hidden flex-col justify-between overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-500 p-10 text-white shadow-2xl lg:flex">
 <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/15 blur-3xl"/>
 <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl"/>
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_55%)]"/>

 <div className="relative">
 <div className="flex items-center gap-3">
 <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-inset ring-white/30 backdrop-blur">
 <svg
 className="h-6 w-6"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2.5"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="9"/>
 <path d="M12 8v4l3 3"/>
 </svg>
 </div>
 <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/30 backdrop-blur">
 Time tracking
 </span>
 </div>

 <h1 className="mt-8 text-5xl font-bold leading-[1.05] tracking-tight">
 Reset your
 <br />
 <span className="bg-gradient-to-r from-emerald-100 via-emerald-100 to-white bg-clip-text text-transparent">
 password.
 </span>
 </h1>
 <p className="mt-4 max-w-md text-base leading-relaxed text-emerald-100">
 Enter your email and we&apos;ll send you a reset link.
 </p>
 </div>

 <div className="relative mt-10">
 <button
 onClick={() => navigate("/")}
 className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-inset ring-white/20 backdrop-blur transition hover:bg-white/20">
 <svg
 className="h-4 w-4"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M19 12H5M12 5l-7 7 7 7"/>
 </svg>
 Back to Login
 </button>
 </div>
 </aside>

 {/* RIGHT — form */}
 <section className="flex items-center justify-center">
 <div className="w-full max-w-md">
 <div className="rounded-3xl border border-white/60 bg-white/80 p-7 shadow-2xl backdrop-blur-xl sm:p-9">
 <header className="mb-6">
 <p className="text-[11px] font-semibold uppercase tracking-wider text-role-primary">
 Reset password
 </p>
 <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
 Reset your password
 </h2>
 <p className="mt-1 text-sm text-slate-500">
 Enter your work email to receive a reset link.
 </p>
 </header>

 {error && (
 <div
 role="alert"className="mb-5 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
 <span className="mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-rose-500"/>
 <span className="flex-1">{error}</span>
 </div>
 )}

 {sent ? (
 <div className="space-y-4">
 <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
 <p className="font-semibold">Check your inbox!</p>
 <p className="mt-1 text-green-700">
 If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
 </p>
 </div>

 <button
 type="button"onClick={() => navigate("/")}
 className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:shadow-emerald-500/20 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-2">
 <span
 className="pointer-events-none absolute inset-0 bg-white/20 opacity-0 transition group-hover:opacity-100"aria-hidden="true"/>
 <span className="relative">Back to Login</span>
 <svg
 className="relative h-4 w-4 transition group-hover:translate-x-0.5"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M5 12h14M13 5l7 7-7 7"/>
 </svg>
 </button>
 </div>
 ) : (
 <form onSubmit={handleSubmit} noValidate className="space-y-4">
 <div className="flex flex-col gap-1.5">
 <label htmlFor="email"className={labelClass}>
 Email
 </label>
 <div className="relative">
 <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
 <svg
 className="h-4 w-4"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
 <path d="M22 6l-10 7L2 6"/>
 </svg>
 </span>
 <input
 id="email"type="email"autoComplete="email"className={`${inputClass} pl-9`}
 placeholder="you@company.com"value={email}
 onChange={(e) => setEmail(e.target.value)}
 disabled={submitting}
 required
 />
 </div>
 </div>

 <button
 type="submit"disabled={submitting}
 className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:shadow-emerald-500/20 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60">
 <span
 className="pointer-events-none absolute inset-0 bg-white/20 opacity-0 transition group-hover:opacity-100"aria-hidden="true"/>
 {submitting && (
 <svg
 className="relative h-4 w-4 animate-spin"viewBox="0 0 24 24"fill="none">
 <circle
 className="opacity-25"cx="12"cy="12"r="10"stroke="currentColor"strokeWidth="4"/>
 <path
 className="opacity-75"fill="currentColor"d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
 </svg>
 )}
 <span className="relative">
 {submitting ?"Sending...":"Send Reset Link"}
 </span>
 </button>

 <p className="text-center text-xs text-slate-500">
 Remember your password?{""}
 <button
 type="button"onClick={() => navigate("/")}
 className="font-medium text-role-primary hover:underline">
 Back to Login
 </button>
 </p>
 </form>
 )}
 </div>
 </div>
 </section>
 </div>
 </div>
 </div>
 );
}